import { Highlighter } from 'react-bootstrap-typeahead';

import { getSchedulingWindowsLOV } from 'api/scheduling_windows/operations/getSchedulingWindowsLOV';

import { EditReference } from './EditReference';

// The minimal identifying shape this control deals in. The backend
// validates the wire format as `zone_name|window_name`, so those are
// the two fields the editable entity needs. `SchedulingWindowSummary`
// (with scheduling_window_id) is structurally assignable to this, so
// the loader passes straight through.
export interface SchedulingWindowReference {
  readonly zone_name: string;
  readonly name: string;
}

interface Props {
  readonly value: SchedulingWindowReference | null;
  readonly onChange: (value: SchedulingWindowReference | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

// `${zone_name}|${name}` matches the wire format and is unique by
// (zone, name) — adequate for both React keys and selection match.
const getKey = (w: SchedulingWindowReference) => `${w.zone_name}|${w.name}`;

const getLabel = (w: SchedulingWindowReference): string => w.name;

/**
 * Searchable dropdown for SchedulingWindows. The dropdown row is a
 * two-column layout — name on the left, zone right-aligned and muted
 * as informational context — but the search only matches on name,
 * which we found is the natural UX when zones live in the
 * background.
 */
export const EditSchedulingWindowReference = ({ value, onChange, label = 'Scheduling Window', mandatory, disabled }: Props) => (
  <EditReference<SchedulingWindowReference>
    label={label}
    value={value}
    onChange={onChange}
    source={getSchedulingWindowsLOV}
    getKey={getKey}
    getLabel={getLabel}
    mandatory={mandatory}
    disabled={disabled}
    renderMenuItemChildren={(w, query) => (
      // <Highlighter> renders into a Fragment; wrap in a <span> so
      // the matched + unmatched pieces collapse into a single flex
      // item. Zone is informational only — plain text, no highlight.
      <div className="d-flex justify-content-between gap-3">
        <span>
          <Highlighter search={query}>{w.name}</Highlighter>
        </span>
        <span className="text-body-secondary">{w.zone_name}</span>
      </div>
    )}
  />
);
